'use client'
import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useSession, signOut } from 'next-auth/react';
import Link from 'next/link';
import Image from 'next/image';

export default function NearbyFriendsPage() {
    const router = useRouter();
    const { data: session, status } = useSession();
    const [nearbyFriendsData, setNearbyFriendsData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searching, setSearching] = useState(false);
    const [currentAddress, setCurrentAddress] = useState('');

    useEffect(() => {
        if (status === 'unauthenticated') {
            router.push('/login');
            return;
        }

        if (status === 'authenticated') {
            fetchNearbyFriends();
        }
    }, [status, router]);

    const fetchNearbyFriends = async () => {
        setSearching(true);
        setLoading(true);

        try {
            const response = await fetch('/api/profiles/nearby', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({})
            });

            const data = await response.json();

            if (data.success) {
                setNearbyFriendsData(data.nearbyFriends);
                setCurrentAddress(data.currentAddress);
            } else {
                alert(data.error || 'Failed to find nearby friends');
            }
        } catch (error) {
            console.error('Error finding nearby friends:', error);
            alert('Failed to find nearby friends');
        } finally {
            setSearching(false);
            setLoading(false);
        }
    };

    const handleLogout = async () => {
        await signOut({ redirect: true, callbackUrl: '/' });
    };

    const currentUser = session?.user;

    if (status === 'loading' || loading) {
        return (
            <div className="profile_fo_loading">
                <div className="profile_fo_loading-spinner"></div>
                <p>Loading nearby friends...</p>
            </div>
        );
    }

    return (
        <div className="profile_fo_container">
            {/* Header */}
            <header className="profile_fo_header">
                <div className="profile_fo_header-content">
                    <h1 className="profile_fo_title">
                        Nearby Friends
                        <span className="profile_fo_subtitle">નજીકના મિત્રો</span>
                    </h1>

                    <div className="profile_fo_user-actions">
                        {currentUser ? (
                            <>
                                <div className="profile_fo_user-info">
                                    <span className="profile_fo_welcome">Welcome,</span>
                                    <span className="profile_fo_user-name">{currentUser.name}</span>
                                </div>
                                {currentUser.role === 'master_admin' ? (
                                    <Link href="/admin/dashboard" className="profile_fo_button profile_fo_button-primary">Dashboard</Link>
                                ) : (
                                    currentUser.id && (
                                        <Link href={`/profile/${currentUser.id}`} className="profile_fo_button profile_fo_button-primary">My Profile</Link>
                                    )
                                )}
                                <Link
                                    href="/profiles"
                                    className="profile_fo_button profile_fo_button-primary"
                                    style={{ backgroundColor: '#FF9800' }}
                                >
                                    All Profiles
                                </Link>
                                <button
                                    onClick={handleLogout}
                                    className="profile_fo_button profile_fo_button-secondary"
                                >
                                    Logout
                                </button>
                            </>
                        ) : (
                            <div>
                                <Link href="/login" className="profile_fo_button profile_fo_button-primary">Login</Link>
                                <Link href="/registration" className="profile_fo_button profile_fo_button-secondary">Register</Link>
                            </div>
                        )}
                    </div>
                </div>
            </header>

            <main className="profile_fo_main">
                {/* Search Info Section */}
                <div className="profile_fo_filters">
                    <div className="profile_fo_filters-header">
                        <h3 className="profile_fo_filters-title">
                            Your Location / તમારું સ્થાન
                        </h3>
                        <div className="profile_fo_total-count">
                            <span className="profile_fo_total-label">Found:</span>
                            <span className="profile_fo_total-number">{nearbyFriendsData.length}</span>
                            <span className="profile_fo_total-text">friends</span>
                        </div>
                    </div>

                    <div className="profile_fo_filter-group">
                        <div className="profile_fo_filter-category">
                            <p style={{ fontSize: '1.1rem', color: '#333', fontWeight: '500', margin: '1rem 0' }}>
                                📍 {currentAddress || 'Loading...'}
                            </p>
                            <button
                                onClick={fetchNearbyFriends}
                                disabled={searching}
                                className="profile_fo_button profile_fo_button-primary"
                                style={{ marginTop: '1rem', opacity: searching ? 0.7 : 1 }}
                            >
                                {searching ? '🔍 Searching...' : '🔄 Search Again'}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Users Grid */}
                <div className="profile_fo_profiles-grid">
                    {nearbyFriendsData.map((friend) => (
                        <Link
                            href={`/profile/${friend._id}`}
                            key={friend._id}
                            className="profile_fo_profile-card"
                        >
                            <div className="profile_fo_profile-avatar">
                                {friend.photo ? (
                                    <Image
                                        src={friend.photo}
                                        alt={friend.name}
                                        width={300}
                                        height={300}
                                        quality={100}
                                        loading="lazy"
                                        className="profile_fo_profile-image"
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover'
                                        }}
                                    />
                                ) : (
                                    <div className="profile_fo_profile-initial">
                                        {friend.name?.charAt(0).toUpperCase()}
                                    </div>
                                )}
                            </div>
                            <div className="profile_fo_profile-info">
                                <h3 className="profile_fo_profile-name">
                                    {friend.name} {friend.surname}
                                </h3>
                                <p className="profile_fo_profile-profession">
                                    {friend.address || 'Not specified'}
                                </p>
                                {friend.division && (
                                    <div className="profile_fo_profile-division">
                                        <span className="profile_fo_division-badge">
                                            વર્ગ {friend.division}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </Link>
                    ))}
                </div>

                {nearbyFriendsData.length === 0 && (
                    <div className="profile_fo_empty-state">
                        <div className="profile_fo_empty-icon">🌍</div>
                        <h3 className="profile_fo_empty-title">No Friends Found Nearby</h3>
                        <p className="profile_fo_empty-message">
                            No friends found in your location. Make sure your residential address is set and matches with other users.
                        </p>
                    </div>
                )}
            </main>

            <footer className="profile_fo_footer">
                <p className="profile_fo_footer-text">
                    &copy; આયોજન સમિતિ (SSC બેચ ૨૦૦૧)
                </p>
            </footer>
        </div>
    );
}
